import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { V3_APP_BASE_URL } from '@/constants/Config';
import type {
  IOrdersList,
  IPartcipanrDetails,
  IParticipant,
} from '@/common/interfaces/order.interface';
import { removeDuplicates, removeDuplicatesWithFromTop } from '@/utilities/CommonUtils';
import type { ISuggestedAddress } from '@/common/interfaces/error.interface';

interface ISuggestedAddressErrorRes {
  status: number;
  data: {
    status: string;
    data: {
      suggestions: ISuggestedAddress[] | ISuggestedAddress;
    };
  };
}

interface ISuggestedAddressRes {
  suggestions: ISuggestedAddress[] | ISuggestedAddress;
}

interface IValidateAddressErrorRes {
  data: {
    message: string;
    status: string;
  };
}

interface ISalesTaxError {
  status: number;
  data: {
    message: string;
    status: string;
  };
}

export interface ICSVSignedURLRes {
  data: {
    fileName?: string;
    signedRequest?: string;
    url?: string;
  };
  message?: string;
}

interface IOrderSectionResp {
  orders: {
    rows: IOrdersList[];
    count: number;
  };
}

interface IErrRes {
  status: string | number;
  data: { message: string };
}

interface IUploadCSVReq {
  file: File;
  fileType: string;
  url: string;
}

/** Defining a service using a base URL and expected endpoints  */
export const orderApi = createApi({
  reducerPath: 'orderAPi',
  tagTypes: ['OrderCreation'],
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  endpoints: (build) => ({
    getParticipantsDetails: build.query<IPartcipanrDetails, string>({
      query: (orderId) => ({ url: `api/order/tracking-details/${orderId}`, method: 'get' }),
    }),
    getOrderParticipants: build.query<
      { count: number; participants: IParticipant[] },
      { orderId: string; limit: number; offset: number }
    >({
      query: ({ orderId, limit, offset }) => ({
        url: `api/cp/group-order-participant/${orderId}?limit=${limit}&offset=${offset}`,
        method: 'get',
      }),
      transformResponse: (response: { count: number; participants: IParticipant[] }) => ({
        count: response.count,
        participants: response.participants,
      }),
      /** One cache entry per order so paging merges stay scoped to the order being viewed. */
      serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs.orderId}`,
      /** Prefer the latest participant row when refetching (e.g. after edit-participant save). */
      merge: (currentCache, newItems) => {
        currentCache.count = newItems.count;
        currentCache.participants.push(...newItems.participants);
        currentCache.participants = removeDuplicatesWithFromTop(currentCache.participants, 'id');
      },
      /** Refetch when the page arg changes */
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    /**
     * Page-based participant list for permanent stores.
     * `offset` is the 1-based page number; the API paginates at `limit` (25).
     */
    getPermanentStoreOrderParticipants: build.query<
      { count: number; participants: IParticipant[] },
      {
        orderId: string;
        limit: number;
        offset: number;
        queryString?: string;
        dateFilter?: string;
        dateFrom?: string;
        dateTo?: string;
      }
    >({
      query: ({ orderId, limit, offset, queryString, dateFilter, dateFrom, dateTo }) => {
        const params = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
        });

        if (queryString) {
          params.set('queryString', queryString);
        }
        if (dateFilter && dateFilter !== 'all') {
          params.set('dateFilter', dateFilter);
        }
        if (dateFrom) {
          params.set('from', dateFrom);
        }
        if (dateTo) {
          params.set('to', dateTo);
        }

        return {
          url: `api/cp/group-order-participant/${orderId}?${params.toString()}`,
          method: 'get',
        };
      },
      transformResponse: (response: {
        count?: number;
        participants?: IParticipant[];
        data?: { count?: number; participants?: IParticipant[] };
      }) => {
        const payload = response.participants ? response : response.data ?? {};

        return {
          count: payload.count ?? 0,
          participants: payload.participants ?? [],
        };
      },
    }),
    downloadGroupOrderCsv: build.query({
      query: (orderId) => ({ url: `api/order/downloadGroupOrderCSV/${orderId}`, method: 'get' }),
    }),
    addFeedBack: build.mutation({
      query: (data) => ({
        url: `api/cp/feedback`,
        method: 'POST',
        data,
      }),
    }),
    getFeedBack: build.query({
      query: (userId) => ({
        url: `api/cp/feedback/${userId}`,
        method: 'get',
      }),
    }),
    getOrders: build.query({
      query: ({ limit, page }) => ({
        url: `api/cp/orders?limit=${limit}&page=${page}`,
        method: 'get',
      }),
      providesTags: ['OrderCreation'],
      transformResponse: (response: IOrderSectionResp) => ({
        orders: response.orders.rows,
        count: response.orders.count,
      }),
      /** Only have one cache entry because the arg always maps to one string */
      serializeQueryArgs: ({ endpointName }) => endpointName,
      /** Always merge incoming data to the cache entry */
      merge: (currentCache, newItems) => {
        currentCache.orders.push(...newItems.orders);
        currentCache.orders = removeDuplicates(currentCache.orders, 'id');
      },
      /** Refetch when the page arg changes */
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    reOpenOrderStore: build.mutation({
      query: ({ id }) => ({
        url: `api/cp/order/reopen-failed-order-email/${id}`,
        method: 'GET',
      }),
      transformResponse: () => ({
        message: 'Email sent successfully.',
        status: 'success',
      }),
      transformErrorResponse: (response: IErrRes) => ({
        message: response.data.message,
        status: 'error',
      }),
    }),
    validateAddress: build.mutation({
      query: (address) => ({
        url: `api/v4/validate-address`,
        method: 'GET',
        params: address,
      }),
      transformResponse: (response: ISuggestedAddressRes) => {
        if (response?.suggestions || response?.suggestions === null) {
          return {
            error: {
              status: 'error',
              data: {
                suggestions: response.suggestions,
              },
            },
          };
        }

        return {
          message: 'Success',
          status: 'success',
          data: response,
        };
      },
      transformErrorResponse: (response: ISuggestedAddressErrorRes) => ({
        ...response,
        data: response.data.data,
      }),
    }),
    validateAddressCSV: build.mutation({
      query: (address) => ({
        url: `api/address/validate-addresses`,
        method: 'POST',
        data: address,
      }),
      transformResponse: () => ({
        message: 'Success',
        status: 'success',
      }),
      transformErrorResponse: (response: IValidateAddressErrorRes) => ({
        message: response.data.message,
        status: response.data.status,
      }),
    }),
    calculateSalesTax: build.mutation({
      query: (data) => ({
        url: `api/cp/order/calculate-sales-tax`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: {
        salesTax: {
          salesTaxAmount: number;
          discountedSalesTaxAmount: number;
        };
      }) => ({
        salesTax: response.salesTax,
        status: 'success',
      }),
      transformErrorResponse: (response: ISalesTaxError) => ({
        message: response.data.message,
        status: response.data.status,
      }),
    }),
    createRegularOrder: build.mutation({
      /**
       * Just for testing purpose
       * @param data simulateError: true to simulate error
       * @returns Error
       */
      query: (data) => ({
        url: `api/cp/order/${data.simulateError ? 'simulateError' : ''}`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: {
        order: { id: number; created_by: string; created_by_id: number };
        invoiceId: number;
        paymentDetails: { transactionId: string } | null;
      }) => ({
        id: response.order.id,
        status: 'success',
        message: 'Order created successfully.',
        created_by: response.order.created_by,
        created_by_id: response.order.created_by_id,
        invoiceId: response.invoiceId,
        orderId: response.order.id,
        transactionId: response.paymentDetails?.transactionId ?? null,
      }),
      invalidatesTags: ['OrderCreation'],
    }),
    getPreSignedURLForCSV: build.mutation({
      query: (data) => ({
        url: 'api/cp/order/csv/get-pre-signed-url',
        method: 'POST',
        data: { ...data },
      }),
      transformResponse: (response) => response,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
    deleteCSVFromS3: build.mutation({
      query: (data) => ({
        url: 'api/cp/order/csv',
        method: 'DELETE',
        data: { ...data },
      }),
      transformResponse: (response) => response,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
    uploadOrderCSV: build.mutation({
      query: (data: IUploadCSVReq) => ({
        url: data.url,
        method: 'put',
        data: { ...data },
        headers: {
          'x-amz-acl': 'private',
          'Content-Type': data.fileType,
          'Sec-Fetch-Dest': 'empty',
          'Sec-Fetch-Mode': 'cors',
          'Sec-Fetch-Site': 'cross-site',
        },
      }),
    }),
    updateGroupOrder: build.mutation({
      query: ({ data, id }) => ({
        url: `api/cp/order/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['OrderCreation'],
    }),
    getCollegiateLicensingInfo: build.query({
      query: () => ({
        url: `api/cp/order/collegiate-licensing-info`,
        method: 'get',
      }),
    }),
    createOrderInRedisCache: build.mutation({
      query: (data) => ({
        url: `api/cp/order-cache`,
        method: 'POST',
        data,
      }),
    }),
    updateOrderInRedisCache: build.mutation({
      query: (data) => ({
        url: `api/cp/order-cache/${data.orderId}`,
        method: 'PUT',
        data: data.checkoutFormData,
      }),
    }),
    getCachedOrder: build.query({
      query: (uniqueOrderId) => ({
        url: `api/cp/order-cache/${uniqueOrderId}`,
        method: 'get',
      }),
    }),
  }),
});

/** Export hooks for usage in functional components, which are auto-generated based on the defined endpoints */
export const {
  useGetParticipantsDetailsQuery,
  useGetOrderParticipantsQuery,
  useGetPermanentStoreOrderParticipantsQuery,
  useLazyDownloadGroupOrderCsvQuery,
  useAddFeedBackMutation,
  useGetFeedBackQuery,
  useGetOrdersQuery,
  useReOpenOrderStoreMutation,
  useValidateAddressMutation,
  useValidateAddressCSVMutation,
  useCalculateSalesTaxMutation,
  useCreateRegularOrderMutation,
  useDeleteCSVFromS3Mutation,
  useGetPreSignedURLForCSVMutation,
  useUpdateGroupOrderMutation,
  useGetCollegiateLicensingInfoQuery,
  useLazyGetCollegiateLicensingInfoQuery,
  useCreateOrderInRedisCacheMutation,
  useLazyGetCachedOrderQuery,
  useUpdateOrderInRedisCacheMutation,
} = orderApi;
